import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { adminAPI } from '../utils/api';
import { toast } from 'sonner';
import { 
  Mail, 
  Server, 
  Save, 
  TestTube, 
  AlertCircle, 
  CheckCircle2, 
  Settings,
  Eye,
  EyeOff,
  ArrowLeft,
  Loader2,
  Users,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const AdminEmailSettingsPage = () => {
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    use_tls: true,
    use_ssl: false,
    sender_name: 'Formlio',
    verification_email: '',
    notification_email: '',
    brand_color: '#4F46E5',
    smtp_configured: false
  });
  
  const [status, setStatus] = useState({
    pending_verifications: 0,
    unverified_users: 0,
    smtp_configured: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        adminAPI.getEmailSettings(),
        adminAPI.getEmailStatus()
      ]);
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to load email settings', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (settings.smtp_configured) {
      if (!settings.smtp_host || !settings.smtp_username) {
        toast.error('SMTP host and username are required when enabling email');
        return;
      }
    }

    setSaving(true);
    try {
      await adminAPI.updateEmailSettings(settings);
      toast.success('Email settings saved successfully');
      loadSettings(); // Refresh status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setTesting(true);
    try {
      await adminAPI.testEmailSettings(testEmail);
      toast.success('Test email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin" className="text-slate-500 hover:text-slate-700 transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-heading font-bold text-slate-900" data-testid="email-settings-title">
                Email Settings
              </h1>
              <p className="text-slate-600 mt-1">Configure SMTP for verification and notification emails</p>
            </div>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">SMTP Status</CardTitle>
              <Server className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                {status.smtp_configured ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <span className="text-green-600 font-medium">Configured</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    <span className="text-amber-600 font-medium">Not Configured</span>
                  </>
                )}
              </div>
              {status.smtp_host && (
                <p className="text-xs text-muted-foreground mt-1">{status.smtp_host}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unverified Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{status.unverified_users}</div>
              <p className="text-xs text-muted-foreground">Awaiting email verification</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Codes</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{status.pending_verifications}</div>
              <p className="text-xs text-muted-foreground">Active verification codes</p>
            </CardContent>
          </Card>
        </div>

        {/* Enable/Disable Email Verification */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <Mail className="h-5 w-5" />
                  <span>Email Verification</span>
                </CardTitle>
                <CardDescription className="mt-1">
                  Enable to require email verification for new signups
                </CardDescription>
              </div>
              <Switch
                checked={settings.smtp_configured}
                onCheckedChange={(checked) => handleChange('smtp_configured', checked)}
                data-testid="smtp-enabled-switch"
              />
            </div>
          </CardHeader>
          {!settings.smtp_configured && (
            <CardContent>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">Email verification is disabled</p>
                    <p className="text-sm text-amber-700 mt-1">
                      New users can sign up without verifying their email. Enable and configure SMTP to require verification.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* SMTP Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="h-5 w-5" />
              <span>SMTP Configuration</span>
            </CardTitle>
            <CardDescription>
              Configure your SMTP server for sending emails
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* SMTP Server */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_host">SMTP Host</Label>
                <Input
                  id="smtp_host"
                  placeholder="smtp.gmail.com"
                  value={settings.smtp_host || ''}
                  onChange={(e) => handleChange('smtp_host', e.target.value)}
                  data-testid="smtp-host-input"
                />
                <p className="text-xs text-muted-foreground">
                  Common: smtp.gmail.com, smtp.office365.com, smtp.mailgun.org
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_port">SMTP Port</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  placeholder="587"
                  value={settings.smtp_port}
                  onChange={(e) => handleChange('smtp_port', parseInt(e.target.value) || 587)}
                  data-testid="smtp-port-input"
                />
                <p className="text-xs text-muted-foreground">
                  Common: 587 (TLS), 465 (SSL), 25 (unencrypted)
                </p>
              </div>
            </div>

            {/* Credentials */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_username">SMTP Username</Label>
                <Input
                  id="smtp_username"
                  type="email"
                  placeholder="your-email@gmail.com"
                  value={settings.smtp_username || ''}
                  onChange={(e) => handleChange('smtp_username', e.target.value)}
                  data-testid="smtp-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_password">SMTP Password / App Password</Label>
                <div className="relative">
                  <Input
                    id="smtp_password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={settings.smtp_password || ''}
                    onChange={(e) => handleChange('smtp_password', e.target.value)}
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  For Gmail, use an App Password (not your regular password)
                </p>
              </div>
            </div>

            {/* Security Options */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Switch
                  id="use_tls"
                  checked={settings.use_tls}
                  onCheckedChange={(checked) => handleChange('use_tls', checked)}
                  data-testid="smtp-tls-switch"
                />
                <Label htmlFor="use_tls">Use TLS (recommended)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="use_ssl"
                  checked={settings.use_ssl}
                  onCheckedChange={(checked) => handleChange('use_ssl', checked)}
                  data-testid="smtp-ssl-switch"
                />
                <Label htmlFor="use_ssl">Use SSL</Label>
              </div>
            </div>

            <Separator />

            {/* Sender Settings */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900">Sender Settings</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender_name">Sender Name</Label>
                  <Input
                    id="sender_name"
                    placeholder="Formlio"
                    value={settings.sender_name || ''}
                    onChange={(e) => handleChange('sender_name', e.target.value)}
                    data-testid="sender-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brand_color">Brand Color</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="brand_color"
                      type="color"
                      value={settings.brand_color || '#4F46E5'}
                      onChange={(e) => handleChange('brand_color', e.target.value)}
                      className="w-16 h-10 p-1 cursor-pointer"
                      data-testid="brand-color-input"
                    />
                    <Input
                      value={settings.brand_color || '#4F46E5'}
                      onChange={(e) => handleChange('brand_color', e.target.value)}
                      className="flex-1"
                      placeholder="#4F46E5"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="verification_email">Verification Email (From)</Label>
                  <Input
                    id="verification_email"
                    type="email"
                    placeholder="noreply@yourdomain.com"
                    value={settings.verification_email || ''}
                    onChange={(e) => handleChange('verification_email', e.target.value)}
                    data-testid="verification-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used for verification codes and password resets
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notification_email">Notification Email (From)</Label>
                  <Input
                    id="notification_email"
                    type="email"
                    placeholder="notifications@yourdomain.com"
                    value={settings.notification_email || ''}
                    onChange={(e) => handleChange('notification_email', e.target.value)}
                    data-testid="notification-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used for form submission notifications
                  </p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Test Email */}
        {settings.smtp_configured && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TestTube className="h-5 w-5" />
                <span>Test Email</span>
              </CardTitle>
              <CardDescription>
                Send a test email to verify your SMTP configuration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end space-x-4">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="test_email">Recipient Email</Label>
                  <Input
                    id="test_email"
                    type="email"
                    placeholder="your-email@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    data-testid="test-email-input"
                  />
                </div>
                <Button 
                  onClick={handleTestEmail} 
                  disabled={testing || !testEmail}
                  variant="outline"
                  data-testid="send-test-email-btn"
                >
                  {testing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Test Email
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Setup Guides */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Setup Guides</CardTitle>
            <CardDescription>Common SMTP provider configurations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4 hover:border-indigo-300 transition-colors">
                <h4 className="font-medium text-slate-900 mb-2">Gmail</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>Host: smtp.gmail.com</li>
                  <li>Port: 587 (TLS)</li>
                  <li>Use App Password</li>
                </ul>
              </div>
              <div className="border rounded-lg p-4 hover:border-indigo-300 transition-colors">
                <h4 className="font-medium text-slate-900 mb-2">Outlook/Office365</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>Host: smtp.office365.com</li>
                  <li>Port: 587 (TLS)</li>
                  <li>Use account email</li>
                </ul>
              </div>
              <div className="border rounded-lg p-4 hover:border-indigo-300 transition-colors">
                <h4 className="font-medium text-slate-900 mb-2">Mailgun</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>Host: smtp.mailgun.org</li>
                  <li>Port: 587 (TLS)</li>
                  <li>Use API credentials</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
